import {
  Accordion,
  Button,
  ButtonVariant,
  ColorClass,
  Icon,
  IconIdentifier,
  Input,
  SwitchCard,
  toast,
  Tooltip,
} from '@aus-platform/design-system';
import classNames from 'classnames';
import { filter, isEmpty, isNil } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  type OrganisationLabel,
  useCreateOrganisationLabel,
  useUpdateOrganisationLabel,
} from 'shared/api';
import { ConfirmationModal } from 'shared/components';
import { KEYBOARD_KEY_HINTS } from 'shared/constants';
import { ErrorSlugs, KeyboardKey } from 'shared/enums';
import {
  ITERATION_LABEL_MESSAGES,
  ITERATION_LABELS_ACCORDION_KEY,
  LABEL_MAX_LENGTH,
} from './constants';
import {
  getLabelValidationError,
  isValidOrganisationLabelName,
  stripDisallowedLabelCharacters,
} from './helpers';
import { IterationLabelsAccordionHeader } from './iteration-labels-accordion-header';
import {
  type EditingEntry,
  type IterationLabelsAccordionProps,
} from './types';

const getInitialAccordionEntries = (
  entries: OrganisationLabel[],
): OrganisationLabel[] =>
  [...entries].sort(
    (firstEntry, secondEntry) =>
      Number(secondEntry.isActive) - Number(firstEntry.isActive),
  );

export const IterationLabelsAccordion: React.FC<
  IterationLabelsAccordionProps
> = ({ entries, isLoading, isAccordionActive, onToggleAccordionItem }) => {
  // States.
  const [accordionEntries, setAccordionEntries] = useState<
    OrganisationLabel[]
  >(getInitialAccordionEntries(entries));
  const [searchTerm, setSearchTerm] = useState('');
  const [newEntryLabel, setNewEntryLabel] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newEntryError, setNewEntryError] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<EditingEntry>({
    id: null,
    label: '',
  });
  const [editEntryError, setEditEntryError] = useState<string | null>(null);
  const [pendingDeactivationEntry, setPendingDeactivationEntry] =
    useState<OrganisationLabel | null>(null);

  // Refs.
  const newEntryInputRef = useRef<HTMLInputElement | null>(null);
  const editInputRef = useRef<HTMLInputElement | null>(null);
  const newEntryContainerRef = useRef<HTMLDivElement | null>(null);
  const editContainerRef = useRef<HTMLDivElement | null>(null);

  // Apis.
  const {
    mutate: createOrganisationLabel,
    isPending: isCreatingLabel,
    isError: isCreatingLabelError,
    isSuccess: isCreatingLabelSuccess,
    error: createLabelError,
    reset: resetCreateLabel,
  } = useCreateOrganisationLabel();

  const {
    data: updatedLabelResponse,
    mutate: updateOrganisationLabel,
    isPending: isUpdatingLabel,
    isError: isUpdatingLabelError,
    isSuccess: isUpdatingLabelSuccess,
    error: updateLabelError,
    reset: resetUpdateLabel,
  } = useUpdateOrganisationLabel();

  // Variables.
  const isMutationPending = isCreatingLabel || isUpdatingLabel;

  // Effects.
  useEffect(() => {
    setAccordionEntries(getInitialAccordionEntries(entries));
  }, [entries]);

  useEffect(() => {
    if (!isAccordionActive) {
      resetAccordionState();
    }
  }, [isAccordionActive]);

  // Exit add/edit mode (discard) when clicking outside the active input, mirroring Esc.
  useEffect(() => {
    const isEditing = isAddingNew || !isNil(editingEntry.id);

    if (!isEditing) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (isAddingNew && !newEntryContainerRef.current?.contains(target)) {
        resetNewEntryState();
      }

      if (
        !isNil(editingEntry.id) &&
        !editContainerRef.current?.contains(target)
      ) {
        resetEditingState();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAddingNew, editingEntry.id]);
  
  // Memos.
  const filteredEntries = useMemo(
    () =>
      filter(accordionEntries, ({ name }) =>
        name.trim().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [accordionEntries, searchTerm],
  );

  // Handlers.
  const handleAddNewEntry = () => {
    setIsAddingNew(true);
    setNewEntryError(null);
  };

  const resetEditingState = () => {
    setEditingEntry({
      id: null,
      label: '',
    });
    setEditEntryError(null);
  };

  const resetNewEntryState = () => {
    setIsAddingNew(false);
    setNewEntryLabel('');
    setNewEntryError(null);
  };

  const resetAccordionState = () => {
    setSearchTerm('');
    setPendingDeactivationEntry(null);
    resetNewEntryState();
    resetEditingState();
  };

  const handleDeactivateRequest = (entry: OrganisationLabel) => {
    if (isMutationPending) {
      return;
    }

    if (entry.isActive) {
      setPendingDeactivationEntry(entry);
      return;
    }

    handleToggleActive(entry);
  };

  const handleCloseDeactivateModal = () => {
    setPendingDeactivationEntry(null);
  };

  const handleConfirmDeactivate = () => {
    if (isNil(pendingDeactivationEntry)) {
      return;
    }

    handleToggleActive(pendingDeactivationEntry);
  };

  const updateAccordinEntry = ({
    id,
    name,
    isActive,
  }: OrganisationLabel) => {
    setAccordionEntries((prev) =>
      prev.map((entry) =>
        entry.id === id ? { ...entry, name, isActive } : entry,
      ),
    );
  };

  useEffect(() => {
    if (isCreatingLabelSuccess) {
      toast.success(ITERATION_LABEL_MESSAGES.Saved);
      resetNewEntryState();
      resetCreateLabel();
      return;
    }

    if (isCreatingLabelError) {
      if (
        createLabelError.meta.details?.form?.['slug'] ===
        ErrorSlugs.ObjectAlreadyExists
      ) {
        setNewEntryError(ITERATION_LABEL_MESSAGES.Duplicate);
      } else {
        toast.error(ITERATION_LABEL_MESSAGES.SaveError);
      }
      resetCreateLabel();
    }
  }, [isCreatingLabelSuccess, isCreatingLabelError, createLabelError]);

  useEffect(() => {
    if (isUpdatingLabelSuccess && updatedLabelResponse) {
      updateAccordinEntry(updatedLabelResponse.data);

      if (!isNil(editingEntry.id)) {
        toast.success(ITERATION_LABEL_MESSAGES.Saved);
        resetEditingState();
      } else {
        toast.success(
          updatedLabelResponse.data.isActive
            ? ITERATION_LABEL_MESSAGES.Activated
            : ITERATION_LABEL_MESSAGES.Deactivated,
        );
        handleCloseDeactivateModal();
      }
      resetUpdateLabel();
      return;
    }

    if (isUpdatingLabelError) {
      if (
        updateLabelError.meta.details?.form?.['slug'] ===
        ErrorSlugs.ObjectAlreadyExists
      ) {
        setEditEntryError(ITERATION_LABEL_MESSAGES.Duplicate);
      } else {
        toast.error(ITERATION_LABEL_MESSAGES.SaveError);
      }
      handleCloseDeactivateModal();
      resetUpdateLabel();
    }
  }, [isUpdatingLabelSuccess, isUpdatingLabelError, updateLabelError]);

  const handleSaveNewEntry = () => {
    if (isMutationPending) {
      return;
    }

    const validationError = getLabelValidationError(
      newEntryLabel,
      accordionEntries,
    );

    if (validationError) {
      setNewEntryError(validationError);
      return;
    }

    const trimmedValue = newEntryLabel.trim();

    createOrganisationLabel({ name: trimmedValue });
  };

  const handleStartEdit = (entry: OrganisationLabel) => {
    setEditingEntry({
      id: entry.id,
      label: entry.name,
    });
    setEditEntryError(null);
  };

  const handleSaveEdit = (entryId: string) => {
    if (isMutationPending) {
      return;
    }

    const validationError = getLabelValidationError(
      editingEntry.label,
      accordionEntries,
      entryId,
    );

    if (validationError) {
      setEditEntryError(validationError);
      return;
    }

    const trimmedValue = editingEntry.label.trim();

    updateOrganisationLabel({
      id: entryId,
      name: trimmedValue,
    });
  };

  const handleToggleActive = (entry: OrganisationLabel) => {
    if (isMutationPending) {
      return;
    }

    const nextIsActive = !entry.isActive;

    updateOrganisationLabel({
      id: entry.id,
      isActive: nextIsActive,
    });
  };

  const focusNewEntryInput = () => {
    newEntryInputRef.current?.focus();
  };

  const focusEditInput = () => {
    editInputRef.current?.focus();
  };

  const handleLabelBeforeInput = (
    event: React.FormEvent<HTMLInputElement>,
  ) => {
    const { data } = event.nativeEvent as InputEvent;

    if (!isNil(data) && !isValidOrganisationLabelName(data)) {
      event.preventDefault();
    }
  };

  const handleNewEntryLabelChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setNewEntryLabel(stripDisallowedLabelCharacters(event.target.value));
    setNewEntryError(null);
  };

  const handleEditEntryLabelChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setEditingEntry((prev) => ({
      ...prev,
      label: stripDisallowedLabelCharacters(event.target.value),
    }));
    setEditEntryError(null);
  };

  const handleNewEntryInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KeyboardKey.Enter) {
      event.preventDefault();
      handleSaveNewEntry();
    }
    else if (event.key === KeyboardKey.Escape) {
      event.stopPropagation();
      resetNewEntryState();
    }
  };

  const handleEditEntryInputKeyDown =
    (entryId: string) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();

      if (event.key === KeyboardKey.Enter) {
        event.preventDefault();
        handleSaveEdit(entryId);
      }

      if (event.key === KeyboardKey.Escape) {
        resetEditingState();
      }
    };

  // Variables.
  const newEntryContainerClassName = classNames(
    'iteration-labels__accordion__new-entry',
    {
      'iteration-labels__accordion__new-entry--empty-list':
        isEmpty(filteredEntries),
    },
  );

  const getListItemClassName = (isActive: boolean) =>
    classNames('iteration-labels__accordion__list-item', {
      'iteration-labels__accordion__list-item--inactive': !isActive,
    });

  // Renderers.
  const renderInputMeta = (
    value: string,
    error: string | null,
    excludeId?: string | null,
  ) => {
    const trimmedValue = value.trim();
    const validationError =
      error ??
      (trimmedValue
        ? getLabelValidationError(value, accordionEntries, excludeId)
        : null);

    return (
      <div className="iteration-labels__accordion__meta">
        <span className="iteration-labels__accordion__char-count">
          {value.length}/{LABEL_MAX_LENGTH}
        </span>
        {validationError ? (
          <span className="iteration-labels__accordion__error">
            {validationError}
          </span>
        ) : (
          <span className="iteration-labels__accordion__hint">
            {isEmpty(trimmedValue)
              ? KEYBOARD_KEY_HINTS.Discard
              : KEYBOARD_KEY_HINTS.Save}
          </span>
        )}
      </div>
    );
  };

  const renderLoadingState = () => (
    <div className="iteration-labels__accordion__skeleton">
      {[0, 1, 2].map((row) => (
        <div
          key={row}
          className="iteration-labels__accordion__skeleton-row"
        />
      ))}
    </div>
  );

  return (
    <Accordion.Item
      eventKey={ITERATION_LABELS_ACCORDION_KEY}
      title={
        <IterationLabelsAccordionHeader
          entryCount={accordionEntries.length}
          onClick={onToggleAccordionItem}
        />
      }
      iconSize={14}
    >
      {isLoading ? (
        renderLoadingState()
      ) : isEmpty(accordionEntries) && !isAddingNew ? (
        <div className="iteration-labels__accordion__empty-list">
          <Icon
            identifier={IconIdentifier.InfoCircle}
            size={20}
            colorClass={ColorClass.Neutral200}
          />

          <div className="iteration-labels__accordion__empty-list-text">
            {ITERATION_LABEL_MESSAGES.EmptyState}
          </div>

          <Button
            variant={ButtonVariant.Link}
            rightIconIdentifier={IconIdentifier.Plus}
            iconSize={14}
            onClick={handleAddNewEntry}
            disabled={isMutationPending}
          >
            Add New Entry
          </Button>
        </div>
      ) : (
        <>
          {!isEmpty(accordionEntries) && (
            <div className="iteration-labels__accordion__actions">
              <Input.Text
                placeholder="Search"
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />

              <Button
                variant={ButtonVariant.Link}
                rightIconIdentifier={IconIdentifier.Plus}
                iconSize={14}
                onClick={handleAddNewEntry}
                disabled={
                  isAddingNew || !isNil(editingEntry.id) || isMutationPending
                }
              >
                Add New Entry
              </Button>
            </div>
          )}

          {isAddingNew && (
            <div
              ref={newEntryContainerRef}
              className={newEntryContainerClassName}
              onClick={focusNewEntryInput}
            >
              <Input.Text
                autoFocus
                value={newEntryLabel}
                ref={newEntryInputRef}
                maxLength={LABEL_MAX_LENGTH}
                disabled={isMutationPending}
                onBeforeInput={handleLabelBeforeInput}
                onChange={handleNewEntryLabelChange}
                onKeyDown={handleNewEntryInputKeyDown}
                className="iteration-labels__accordion__input-text"
              />
              {renderInputMeta(newEntryLabel, newEntryError)}
            </div>
          )}

          <div className="iteration-labels__accordion__list">
            {filteredEntries.map((entry: OrganisationLabel) => {
              const isEditingEntry = editingEntry.id === entry.id;

              return (
                <div
                  key={entry.id}
                  ref={isEditingEntry ? editContainerRef : undefined}
                  className={getListItemClassName(entry.isActive)}
                  onClick={focusEditInput}
                >
                  <div className="iteration-labels__accordion__list-item-label">
                    {isEditingEntry ? (
                      <Input.Text
                        autoFocus
                        ref={editInputRef}
                        value={editingEntry.label}
                        maxLength={LABEL_MAX_LENGTH}
                        disabled={isMutationPending}
                        className="iteration-labels__accordion__input-text"
                        onBeforeInput={handleLabelBeforeInput}
                        onChange={handleEditEntryLabelChange}
                        onKeyDown={handleEditEntryInputKeyDown(entry.id)}
                      />
                    ) : (
                      <span>{entry.name}</span>
                    )}
                  </div>

                  <div className="iteration-labels__accordion__list-item-actions">
                    {isEditingEntry ? (
                      renderInputMeta(
                        editingEntry.label,
                        editEntryError,
                        entry.id,
                      )
                    ) : (
                      <>
                        {entry.isActive && (
                          <Tooltip hoverText="Edit">
                            <Icon
                              identifier={IconIdentifier.Pencil}
                              onClick={() => handleStartEdit(entry)}
                              size={14}
                              colorClass={ColorClass.Primary500}
                            />
                          </Tooltip>
                        )}

                        <Tooltip
                          hoverText={entry.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <SwitchCard
                            className="iteration-labels__accordion__switch-card"
                            title=""
                            checked={entry.isActive}
                            onClick={(event: React.MouseEvent) => {
                              event.stopPropagation();
                              handleDeactivateRequest(entry);
                            }}
                          />
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {isEmpty(filteredEntries) && !isAddingNew && (
              <div className="iteration-labels__accordion__empty">
                {ITERATION_LABEL_MESSAGES.NoMatch}
              </div>
            )}
          </div>

          {pendingDeactivationEntry && (
            <ConfirmationModal
              title="Deactivate Custom Entry"
              message={
                <>
                  Are you sure you want to deactivate{' '}
                  <strong>{pendingDeactivationEntry.name}</strong>?
                </>
              }
              confirmText="Yes, Deactivate"
              isConfirmDanger
              isConfirmButtonLoading={isUpdatingLabel}
              onClose={handleCloseDeactivateModal}
              onSubmit={handleConfirmDeactivate}
            />
          )}
        </>
      )}
    </Accordion.Item>
  );
};
